import { useMutation, useQueryClient, QueryKey } from "@tanstack/react-query";

type MutationFn<T> = (variable: T) => Promise<any>;
type OptimisticUpdate<T, D> = (variable: T, oldData?: D) => D | undefined;

export type OptimisticMutationUpdateOptions = {
  preventRefetchOnSettled?: boolean;
  exactQueryKey?: boolean;
};

interface MutationOptions<T, D> {
  queryKey: QueryKey;
  mutationFn: MutationFn<T>;
  optimisticUpdate: OptimisticUpdate<T, D>;
  updateOptions?: OptimisticMutationUpdateOptions;
}

export function useOptimisticMutation<V, D>({
  queryKey,
  mutationFn,
  optimisticUpdate,
  updateOptions = {},
}: MutationOptions<V, D>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onMutate: async (variable: V) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey,
        exact: updateOptions.exactQueryKey,
      });

      // Snapshot all matching queries' data
      const queryFilter = { queryKey, exact: updateOptions.exactQueryKey };
      const previousData = new Map();

      // Get all matching queries to snapshot their data
      const matchingQueries = queryClient.getQueryCache().findAll(queryFilter);
      matchingQueries.forEach((query) => {
        previousData.set(JSON.stringify(query.queryKey), query.state.data);
      });

      // Optimistically update the cache
      queryClient.setQueriesData<D>(queryFilter, (oldData) =>
        optimisticUpdate(variable, oldData)
      );

      // Return the snapshotted values
      return { previousData };
    },
    onError: (_err, _variable, context) => {
      // Rollback each query to its previous value
      if (context?.previousData) {
        context.previousData.forEach((data, queryKeyString) => {
          const queryKey = JSON.parse(queryKeyString);
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      if (!updateOptions.preventRefetchOnSettled) {
        // Refetch after error or success to ensure cache is in sync with server
        queryClient.invalidateQueries({
          queryKey,
          exact: updateOptions.exactQueryKey,
        });
      }
    },
  });
}
